import { useCallback, useMemo, useRef } from 'react';

import { ColorScale } from '../utils/ColorScale';

/**
 * Returns a color scale that associates colors with keys, creating implicit assignments
 * sequentially.
 *
 * @param palette An array of colors to pick from. New implicit assignments come from this array.
 * When the array is exhausted, the array is used again from the beginning.
 * @param fixedAssignments A set of color assignments that override any implicit assignments. Fixed
 * assignments will always override any implicit assignment, even if the same key had previously
 * received an implicit assignment.
 */
export const usePaletteColorScale = <TColor, TKey>(
  palette: readonly TColor[],
  fixedAssignments?: Map<TKey, TColor>,
): ColorScale<TKey, TColor> => {
  const colorMap = useRef(new Map<TKey, TColor>());
  const lastAssignedIndex = useRef(0);

  const get = useCallback(
    (key: TKey) => {
      const color = fixedAssignments?.get(key) ?? colorMap.current.get(key);
      if (color) {
        return color;
      }

      const nextAssignment = palette[lastAssignedIndex.current % palette.length];
      lastAssignedIndex.current += 1;
      colorMap.current.set(key, nextAssignment);

      return nextAssignment;
    },
    [fixedAssignments, palette],
  );

  return useMemo(() => ({ get }), [get]);
};
